/**
 * WorkspaceLocationPopup — corner-anchored metrics card for a selected workspace
 * location on `/map`.
 *
 * Positioning
 * -----------
 * The metrics render as a card portalled into the map chrome so it sits inside
 * the map viewport. `DialogContent position="tl"` plus `MAP_INFO_CARD_CLASS`
 * pin the card under the search column.
 *
 * A Mapbox `Popup` anchored at the pin's lng/lat is deliberately avoided:
 * Mapbox places that popup over the centre of the map, where its opaque card
 * covers the on-map geospatial metric layers (the coloured AADT segments)
 * rendered around the pin. Pinning the card to a fixed map-viewport offset
 * leaves the map — and those layers — fully visible.
 *
 * The card hosts the SHARED `MarkerMetricsBody` — the exact same body the project
 * map renders — wrapped in the SHARED `Dialog`/`DialogContent` shell, which
 * provides the Radix `Dialog` context that body depends on (it renders
 * `DialogTitle` / `DialogDescription` / `DialogPanel`). `showOverlay={false}`
 * keeps it non-modal (no scrim, no scroll-lock) so the rest of the map stays
 * interactive. There is NO `innerHTML` / `setHTML` — all content is React text
 * nodes, keeping it XSS-safe (frontend `locations/` invariant).
 *
 * Project-agnostic: no `projectId` is threaded into the body's analytics
 * context — the map surface carries no project identity.
 *
 * Delete
 * ------
 * The Delete button opens a confirmation modal (`ConfirmDeleteModal`). On
 * confirm, `useRemoveWorkspaceLocation` fires — optimistically removing the
 * marker from the cache — and the popup closes immediately, in the same tick.
 * It does NOT wait for the mutation's `onSuccess`: the optimistic removal
 * already drops this location from the parent's data on that same tick,
 * which unmounts this popup before the mutation can settle, so a per-call
 * `onSuccess` callback would never fire. On server failure the hook rolls
 * the marker back on the map automatically; the popup stays closed (matching
 * the already-optimistic UX — no undo).
 */
import { Trash2, X } from 'lucide-react';
import type { Map as MapboxMap } from 'mapbox-gl';
import { useCallback, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/atoms/dialog';
import { Separator } from '@/components/atoms/separator';
import { Skeleton } from '@/components/atoms/skeleton';
import { ConfirmDeleteModal } from '@/components/molecules/ConfirmDeleteModal';
import { CreateDealDialog } from '@/features/deals/components/CreateDealDialog';
import { HubSpotMark } from '@/features/deals/components/HubSpotMark';
import { MarkerMetricsBody } from '@/features/locations/components/MarkerMetricsBody';
import { MAP_DETAIL_CARD_WIDTH } from '@/features/locations/constants';
import { MAP_INFO_CARD_BODY_CLASS, MAP_INFO_CARD_CLASS } from './mapBarGeometry';
import type { MapLayersService } from '@/features/locations/utils/MapLayersService';
import { RADIUS_VALUES, radiusIndexToValue } from '@/features/locations/utils/radiusHelpers';
import { useRecomputeAtRadius } from '@/features/map/hooks/useRecomputeAtRadius';
import { requestedMetricsFromResults } from '@/features/map/utils/requestedMetricsFromResults';
import { useRemoveWorkspaceLocation } from '@/features/workspaces/services/workspace.api';
import { cn } from '@/libs/utils/cn';
import type { ICapturedAadtExtracted, IProjectResults } from '@/types/api';
import { RadiusSlider } from './RadiusSlider';

interface WorkspaceLocationPopupProps {
  /** Live Mapbox map instance — its container hosts the corner card. */
  map: MapboxMap;
  /**
   * The selected location's full results (location + metrics + optional
   * forecast), or `undefined` while the on-demand results query is pending or
   * disabled (the location's own compute run is still active). When absent
   * the card shows a skeleton so the click/launch gets immediate feedback (no
   * blank gap before the fetch resolves).
   */
  locationData?: IProjectResults;
  /** Per-location layer service from `useLocationDetailLayers` (drives overlay filters). */
  layersService?: MapLayersService;
  /** Pre-extracted captured-AADT summary from `useLocationDetailLayers`. */
  capturedAadtData?: ICapturedAadtExtracted;
  /** Active workspace id — required for the delete mutation (workspace-switch safety). */
  workspaceId: string;
  /** The selected location's id — required for the delete mutation. */
  locationId: string;
  /** Called when the card is dismissed (X, Delete, Escape, or re-clicking the pin). */
  onClose: () => void;
  dialogContainer?: HTMLElement;
  /**
   * Selects the deal a creation just landed. The location has left the workspace by then — the
   * backend excludes a location carrying a deal from the marker set — so this card gives way to
   * the deal's own.
   */
  onDealCreated: (dealId: string) => void;
  /** Live 0–100 percent shown alongside the skeleton while computing. */
  computingProgress?: number | null;
  /** Project id of the active compute run behind this location, or `null`
   *  while not computing (or before the run has a project id yet). Required
   *  for the Cancel button shown in the computing skeleton. */
  computingProjectId?: string | null;
  /** Cancels the active compute run by project id (Cancel button, computing skeleton only). */
  onCancelComputing?: (projectId: string) => void;
  /** Selects a location id — used to follow a radius recompute onto its new location. */
  onSelectLocation: (id: string) => void;
}

export function WorkspaceLocationPopup({
  map,
  locationData,
  layersService,
  capturedAadtData,
  workspaceId,
  locationId,
  computingProgress = null,
  computingProjectId = null,
  onCancelComputing,
  onSelectLocation,
  onClose,
  onDealCreated,
  dialogContainer,
}: WorkspaceLocationPopupProps) {
  const { mutate: removeLocation, isPending: isRemoving } = useRemoveWorkspaceLocation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateDealOpen, setIsCreateDealOpen] = useState(false);
  const recompute = useRecomputeAtRadius(onSelectLocation);

  // Close immediately rather than waiting for the mutation to settle: the
  // parent's optimistic marker removal (`onMutate`) drops this location from
  // the map data on the same tick, which unmounts this popup before the
  // mutation can resolve — so a per-call `onSuccess` would never fire.
  const handleConfirmDelete = useCallback(() => {
    removeLocation({ workspaceId, locationId });
    onClose();
  }, [removeLocation, workspaceId, locationId, onClose]);

  // Recompute at the new radius (same point, same metrics); the fresh location
  // auto-selects on top. No-op if unchanged.
  const handleRadiusCommit = useCallback(
    (radius: number) => {
      if (!locationData || radius === locationData.location.radius) return;
      recompute({
        workspaceId,
        previousLocationId: locationId,
        coordinates: locationData.location.coordinates,
        previousRadius: locationData.location.radius,
        radius,
        metrics: requestedMetricsFromResults(locationData, capturedAadtData),
      });
    },
    [recompute, workspaceId, locationId, locationData, capturedAadtData],
  );

  return (
    <Dialog
      open
      modal={false}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        // Portal into the map chrome so left/top are the map viewport, not the page.
        dialogContainer={dialogContainer ?? map.getContainer()}
        showOverlay={false}
        showClose={false}
        position="tl"
        className={cn(MAP_INFO_CARD_CLASS, 'p-0', MAP_DETAIL_CARD_WIDTH)}
        onInteractOutside={(e) => {
          // Non-modal: clicks on the map must NOT dismiss the card.
          e.preventDefault();
        }}
      >
        {/* Padding is on the scroller: the radius ticks use a negative margin, which becomes a
            horizontal scrollbar when the padding sits outside this box. The metrics side panel is
            portaled onto the dialog, outside the scroller, so it can stick out of the right edge. */}
        <div className={cn(MAP_INFO_CARD_BODY_CLASS, 'p-3')}>
          {locationData ? (
            <div className="flex flex-col">
              {/* Action bar: a dedicated row ABOVE the body so it never overlaps the address title,
                which can wrap to two lines (the body renders full-width below). */}
              <div className="mb-2 flex items-center justify-between gap-2">
                {/* A plain clickable text, sized like the card's "See street view" link, in
                  HubSpot's own #FF7A59 — off-palette on purpose, because this hands the location
                  to the portal rather than acting inside SpotOn. It needs no deal-less guard: the
                  backend keeps a location carrying a deal out of the marker set, so this card only
                  ever renders for one without. */}
                <button
                  type="button"
                  onClick={() => setIsCreateDealOpen(true)}
                  className="deal-link-sheen inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-[#FF7A59] hover:underline"
                >
                  <HubSpotMark className="size-4" />
                  Create deal
                </button>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={onClose}
                  className="rounded-md p-1.5 text-[#626968] hover:text-[#021715] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <MarkerMetricsBody
                locationData={locationData}
                capturedAadtData={capturedAadtData}
                layersService={layersService}
                radiusControl={
                  <RadiusRecomputeControl
                    key={locationId}
                    radius={locationData.location.radius}
                    onCommit={handleRadiusCommit}
                  />
                }
              />
              <Separator className="my-2" />
              <button
                type="button"
                aria-label="Delete location"
                onClick={() => setIsDeleteModalOpen(true)}
                className="inline-flex items-center justify-center gap-1.5 self-center text-sm text-destructive hover:text-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete location
              </button>
            </div>
          ) : (
            <div className="flex w-full flex-col" aria-busy="true" aria-live="polite">
              <DialogTitle className="hidden">Location details</DialogTitle>
              <div className="mb-3.5 flex items-center justify-between gap-1.5">
                <div className="flex items-center gap-1.5 text-[11px] text-primary-500">
                  <span className="size-1.5 shrink-0 animate-pulse rounded-full bg-brand-800" />
                  Computing metrics…{computingProgress != null ? ` ${computingProgress}%` : ''}
                </div>
                <div className="flex items-center gap-1">
                  {computingProjectId != null && onCancelComputing != null && (
                    <button
                      type="button"
                      onClick={() => onCancelComputing(computingProjectId)}
                      className="flex shrink-0 items-center gap-1 text-[11px] text-brand-800 hover:text-brand-900"
                    >
                      <X size={14} aria-hidden />
                      Cancel
                    </button>
                  )}
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="rounded-md p-1 text-[#626968] hover:text-[#021715]"
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              </div>
              <Skeleton className="mb-[9px] h-[11px] w-[60%]" />
              <Skeleton className="mb-[9px] h-[11px] w-[90%]" />
              <Skeleton className="mb-[9px] h-[11px] w-[35%]" />
              <div className="my-3 h-px w-full bg-border" />
              <Skeleton className="h-[30px] w-full rounded-[4px]" />
              <Skeleton className="mt-[14px] mb-[9px] h-[11px] w-[50%]" />
              <Skeleton className="my-3 h-[34px] w-[55%]" />
              <Skeleton className="mb-[9px] h-[11px] w-[35%]" />
              <div className="my-3 h-px w-full bg-border" />
              <Skeleton className="mb-[9px] h-[11px] w-[45%]" />
              {Array.from({ length: 4 }, (_, i) => (
                <div key={i} className="flex items-center justify-between gap-[10px] py-[9px]">
                  <Skeleton className="h-[11px] w-[40%]" />
                  <Skeleton className="h-[7px] w-[42%]" />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        onConfirm={handleConfirmDelete}
        isDeleting={isRemoving}
        title="Delete location"
        heading="Confirm you want to remove this location"
        description="Removing this location will take it off the workspace map"
      />
      {/* Mounted only while open: the dialog owns queries and an SSE subscription, and neither
          should exist for a card nobody opened one on. */}
      {isCreateDealOpen && (
        <CreateDealDialog
          open
          onOpenChange={setIsCreateDealOpen}
          locationId={locationId}
          locationAddress={locationData?.location?.address}
          locationAddressLines={locationData?.location}
          locationCoordinates={locationData?.location?.coordinates}
          locationPoiName={locationData?.point_of_interest?.name}
          locationPoiType={locationData?.point_of_interest?.type}
          workspaceId={workspaceId}
          onCreated={onDealCreated}
        />
      )}
    </Dialog>
  );
}

/**
 * Radius slider (mirrors the add-location panel) over the fixed RADIUS_VALUES
 * stops. Keyed by location id so its index resets to the selected location's
 * radius; commits the chosen radius only on release (`onValueCommit`).
 */
function RadiusRecomputeControl({
  radius,
  onCommit,
}: {
  radius: number;
  onCommit: (radius: number) => void;
}) {
  // Seed to the nearest stop so a legacy radius outside RADIUS_VALUES shows its
  // closest label rather than silently snapping to 500 m.
  const seedIndex = RADIUS_VALUES.reduce(
    (best, value, i) =>
      Math.abs(value - radius) < Math.abs(RADIUS_VALUES[best] - radius) ? i : best,
    0,
  );
  const [index, setIndex] = useState(seedIndex);

  return (
    <RadiusSlider
      index={index}
      onIndexChange={setIndex}
      onIndexCommit={(i) => onCommit(radiusIndexToValue(i))}
    />
  );
}
